import styled from '@emotion/styled';
import { tokens, Text, IconButton } from '@hotjar/hotsauce';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background-color: ${tokens.BRAND_COLORS.cobalt_100};
  border: 1px solid ${tokens.color.ui_border_light};
  border-radius: ${tokens.radius.l};
  margin-bottom: ${tokens.spacer.x3};
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    right: 0;
    top: 50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(
      105% 117.79% at 48.77% 80.34%,
      rgba(161, 143, 214, 0.5) 8.43%,
      rgba(195, 240, 247, 0.5) 58.85%,
      rgba(195, 240, 247, 0) 100%
    );
    filter: blur(100px);
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${tokens.spacer.x2};
  position: relative;
  max-width: 540px;
`;

const MediaContent = styled.div<{ withDismiss?: boolean }>`
  position: relative;
  width: 40%;
  min-height: 184px;
  max-width: 320px;
  padding: ${({ withDismiss }) =>
    withDismiss ?? false
      ? `${tokens.spacer.x1pt5} ${tokens.spacer.x4} ${tokens.spacer.x1pt5} ${tokens.spacer.x1pt5}`
      : `${tokens.spacer.x1pt5}`};
`;

const StyledTitle = styled.h3`
  ${tokens.text('header5')};
  margin-bottom: ${tokens.spacer.x2};
`;

const DismissButton = styled(IconButton)`
  position: absolute;
  top: ${tokens.spacer.x1};
  right: ${tokens.spacer.x1};

  > button {
    width: ${tokens.spacer.x2};
    height: ${tokens.spacer.x2};
  }
`;

export interface GuideCardProps {
  label?: string;
  title: string;
  description: string | JSX.Element;
  media: JSX.Element;
  actions?: JSX.Element;
  onDismiss?: () => void;
}

export const GuideCard = ({
  media,
  label = '',
  title,
  description,
  actions,
  onDismiss,
}: GuideCardProps): JSX.Element => {
  return (
    <Wrapper>
      <TextContent>
        {label ? (
          <Text
            textType="caption"
            textColor="text_light"
            css={{ textTransform: 'uppercase', marginBottom: tokens.spacer.x0 }}
          >
            {label}
          </Text>
        ) : null}
        <StyledTitle>{title}</StyledTitle>
        <Text css={{ marginBottom: tokens.spacer.x2 }} textType="body2" textColor="text_light">
          {description}
        </Text>
        {actions || null}
      </TextContent>
      <MediaContent withDismiss={!!onDismiss}>{media}</MediaContent>
      {onDismiss ? (
        <DismissButton
          data-testid="dismiss-button"
          icon="close"
          kind="ghost"
          tooltipText="Dismiss"
          description="Dismiss"
          onClick={() => onDismiss()}
        />
      ) : null}
    </Wrapper>
  );
};
